import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import { Link } from "react-router";
import axios from 'axios';
import { getProductImage } from '../utils/imageHelper.js';
import { fetchProducts, fetchProductsByFilter } from '../item.js'; 
import { addToCart } from '../Cart-Context/context';
import { slugify } from '../utils/slugify';
import { trackEvent } from '../utils/trackEvents';
import { useSession } from '../Cart-Context/session.js';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { API_BASE_URL } from '../utils/apiConfig.js';

const CATEGORIES = [
    { label: "Bakery", aisles: ["Bagels & English Muffins", "Bread", "Breakfast Pastries", "Buns & Rolls", "Cakes & Pies", "Cookies & Brownies", "Frozen Baked Goods", "More Desserts", "Tortillas & Flatbreads"] },
    { label: "Dairy & Eggs", aisles: ["Butter", "Coffee Creamer", "Cottage Cheese", "Cream", "Eggs", "Half & Half", "Sour Cream & Cream Cheese", "Yogurt"] },
    { label: "Produce", aisles: ["Fresh Fruits", "Fresh Vegetables", "Herbs"] },
    { label: "Frozen", aisles: ["Frozen Breads & Doughs", "Frozen Breakfast", "Frozen Broths & Juice Concentrate", "Frozen Desserts", "Frozen Fruits", "Frozen Meat & Seafood", "Frozen Pizzas & Meals", "Frozen Snacks", "Frozen Vegetables", "Ice Cream & Popsicles"] },
    { label: "Meat & Seafood", aisles: ["Beef", "Chicken", "Fish", "Hot Dogs & Sausages", "Pork", "Shellfish", "Specialty Meats", "Turkey"] },
    { label: "Deli", aisles: ["Cheese", "Deli Meats", "Meat & Cheese Combos", "Olives, Dips, & Spreads", "Tofu & Meat Alternatives"] },
    { label: "Baking Essentials", aisles: ["Baking Chocolates & Morsels", "Baking Milks", "Baking Mixes", "Baking Powders", "Doughs", "Extracts", "Flours", "Frosting & Cake Decorations", "Honey, Syrup, & Sweeteners", "Ice Cream Cones", "Marshmallows", "Pie Crusts & Fillings", "Sugars"] },
    { label: "Snacks & Candy", aisles: ["Chips", "Chocolate & Candy", "Cookies & Sweet Treats", "Crackers", "Dips", "Dried Fruit & Fruit Snacks", "Fruit Snacks", "Fruit Cups & Applesauce", "Gum & Mints", "Jerky", "More Snacks", "Nuts & Trail Mix", "Popcorn & Pretzels", "Pudding & Gelatin", "Snack Bars"] },
    { label: "Beverages", aisles: ["Coffee", "Drink Mixes", "Energy Drinks", "Juice", "Kombucha & Probiotic Drinks", "Milk", "Mixers & Non-Alcoholic Drinks", "Protein Drinks", "Soft Drinks", "Sports Drinks", "Tea", "Water & Sparkling Water"] },
    { label: "Breakfast", aisles: ["Breakfast Bars", "Cereal", "Granola", "Jams & Jellies", "Nut Butters", "Oatmeal", "Pancake & Waffle Mix", "Toaster Pastries"] },
    { label: "Canned Goods & Soups", aisles: ["Broths & Stocks", "Canned Beans", "Canned Coconut", "Canned Fish", "Canned Fruits", "Canned Meals", "Canned Meat", "Canned Vegetables", "Soups"] },
    { label: "Condiments & Sauces", aisles: ["Asian Sauces", "Barbecue Sauce", "Horseradish & Wasabi", "Hot Sauces", "Ketchup", "Mayonnaise", "More Condiments & Sauces", "Mustard", "Olives & Pickled Goods", "Pasta Sauces", "Pastes", "Relish", "Salad Dressings", "Salad Toppings", "Salsa"] },
    { label: "Dry Goods & Pasta", aisles: ["Beans", "Boxed Meals & Sides", "Canned Tomato", "More Dry Goods", "Noodles", "Pasta", "Pasta & Pizza Sauces", "Rices & Grains", "Seeds"] },
    { label: "Oils, Vinegars, & Spices", aisles: ["Boullions", "Bread Crumbs", "Cooking Oils", "Marinades", "Salt & Pepper", "Spices & Seasoning", "Vinegar"] },
    { label: "Prepared Foods", aisles: ["Other Prepared Meats", "Party Platters & Trays", "Pizza & Meals", "Prepared Breakfast", "Ready to Cook Meals", "Salads", "Sandwiches & Wraps", "Snack Packs", "Sushi"] },
    { label: "Beer", aisles: ["Ale", "IPA", "Lager", "More Beer", "Non-Alcoholic Beers", "Stout", "Variety Packs"] },
    { label: "Hard Beverages", aisles: ["Flavored Malt Beverages", "Hard Seltzers", "Hard Tea", "Ready-to-Drink"] },
    { label: "Liquor", aisles: ["Brandy & Cognac", "Gin", "Liqueurs", "More Liquor", "Rum", "Tequila", "Vodka", "Whiskey"] },
    { label: "Wine", aisles: ["Champagne & Sparkling Wine", "More Wine", "Red Wine", "Rose", "White Wine"] },
];

const NUTRITION_GROUPS = [
    {
        label: "Calories",
        column: "calories",
        options: [
            { label: "Under 100", op: "<", val: 100 },
            { label: "100 - 200", op: "BETWEEN", val: 100, val2: 200 },
            { label: "200 - 300", op: "BETWEEN", val: 200, val2: 300 },
            { label: "300+", op: ">", val: 300 },
        ]
    },
    {
        label: "Sodium (mg)",
        column: "sodium",
        options: [
            { label: "Low (<140mg)", op: "<", val: 140 },
            { label: "Moderate (140-400mg)", op: "BETWEEN", val: 140, val2: 400 },
            { label: "High (400mg+)", op: ">", val: 400 },
        ]
    },
    {
        label: "Protein (g)",
        column: "protein",
        options: [
            { label: "Low (<5g)", op: "<", val: 5 },
            { label: "Moderate (5-15g)", op: "BETWEEN", val: 5, val2: 15 },
            { label: "High (15g+)", op: ">", val: 15 },
        ]
    },
    {
        label: "Sugar (g)",
        column: "totalSugars",
        options: [
            { label: "Low (<5g)", op: "<", val: 5 },
            { label: "Moderate (5-15g)", op: "BETWEEN", val: 5, val2: 15 },
            { label: "High (15g+)", op: ">", val: 15 },
        ]
    },
    {
        label: "Fiber (g)",
        column: "fiber",
        options: [
            { label: "Low (<2g)", op: "<", val: 2 },
            { label: "Source of (2-5g)", op: "BETWEEN", val: 2, val2: 5 },
            { label: "High (5g+)", op: ">", val: 5 },
        ]
    }
];

const CategoryDropdown = ({ cat, sessionId, experimentId }) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const timeoutRef = useRef(null);

    const handleMouseEnter = () => {
        clearTimeout(timeoutRef.current);
        setOpen(true);
    };

    const handleMouseLeave = (e) => {
        timeoutRef.current = setTimeout(() => {
            const overWrapper = wrapperRef.current?.contains(document.elementFromPoint(e.clientX, e.clientY));
            const overDropdown = dropdownRef.current?.contains(document.elementFromPoint(e.clientX, e.clientY));
            if (!overWrapper && !overDropdown) {
                setOpen(false);
            }
        }, 100);
    };

    const dropdownPortal = open && wrapperRef.current && createPortal(
        <div
            ref={dropdownRef}
            className="aisle-dropdown"
            style={{
                position: 'fixed',
                top: wrapperRef.current.getBoundingClientRect().bottom - 1,
                left: wrapperRef.current.getBoundingClientRect().left,
                minWidth: 200,
                zIndex: 9999,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="aisle-dropdown-header">Aisles</div>
            {cat.aisles.map((aisle) => (
                <Link
                    key={aisle}
                    className="aisle-item"
                    to={`/products/aisle/${encodeURIComponent(aisle)}`}
                    state={{ category: cat.label, aisle }}
                    onClick={() => {
                        trackEvent(sessionId, 'CLICK', 'Aisle Filter', { category: cat.label, aisle: aisle }, experimentId);
                        setOpen(false);
                    }}
                >
                    › {aisle}
                </Link>
            ))}
        </div>,
        document.body
    );

    return (
        <div
            className="category-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Link
                className="category-btn"
                to={`/products/category/${cat.label}`}
                state={{ category: cat.label }}
                onClick={() => {
                    trackEvent(sessionId, 'CLICK', 'Category Filter', { category: cat.label }, experimentId);
                    setOpen(false);
                }}
            >
                {cat.label}
                <span className={`category-chevron ${open ? 'open' : ''}`}>▾</span>
            </Link>
            {dropdownPortal}
        </div>
    );
};

const Products = () => {
    const { sessionId, experimentId } = useSession();
    const [products, setProducts] = useState([]);
    const [popup, setPopup] = useState("");
    const [activeFilter, setActiveFilter] = useState(null); 
    const [expandedGroup, setExpandedGroup] = useState(null); 
    const [showFilters, setShowFilters] = useState(true);
    const [showCategories, setShowCategories] = useState(true);
    const [showQuickInfo, setShowQuickInfo] = useState(true); 
    const [restrictionMode, setRestrictionMode] = useState('HIDE');
    
    const [currentPage, setCurrentPage] = useState(() => {
        return parseInt(sessionStorage.getItem('lastProductsPage')) || 1;
    });

    const [totalPages, setTotalPages] = useState(1); 
    const [jumpPage, setJumpPage] = useState(""); 
    const [isLoading, setIsLoading] = useState(false); 
    const categoryScrollRef = useRef(null);
    const [activeNutritionId, setActiveNutritionId] = useState(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/active-experiment`);
            
                // Check if the current session matches the active experiment ID
                const isParticipant = res.data && Number(experimentId) === Number(res.data.experimentId);

                if (isParticipant) {
                    setShowFilters(res.data.showFilters === 1);
                    setShowCategories(res.data.showCategories === 1);
                    setShowQuickInfo(res.data.showQuickInfo === 1);
                    setRestrictionMode(res.data.restrictionMode || 'HIDE');
                } else {
                    // Default to showing everything for non-participants
                    setShowFilters(true);
                    setShowCategories(true);
                    setShowQuickInfo(true);
                    setRestrictionMode('HIDE');
                }
            } catch (err) {
                setShowFilters(true);
                setShowCategories(true);
                setShowQuickInfo(true);
                setRestrictionMode('HIDE');
            }
        };
        fetchConfig();
    }, [experimentId]);

    const toggleNutrition = (product) => {
        const currentId = product.id;
        const isOpening = activeNutritionId !== currentId;
        if (isOpening) {
            trackEvent(sessionId, 'CLICK', 'Quick Nutrition Open', {
                productId: currentId,
                productName: product.name
            }, experimentId);
        }
        setActiveNutritionId(isOpening ? currentId : null);
    };

    useEffect(() => {
        const loadProducts = async () => {
            setIsLoading(true);
            try {
                let data;
                if (activeFilter) {
                    data = await fetchProductsByFilter(
                        activeFilter.column, 
                        activeFilter.op, 
                        activeFilter.val, 
                        currentPage,
                        null, 
                        null, 
                        activeFilter.val2 
                    );
                    setProducts(data.products);
                    setTotalPages(data.totalPages || 1);
                } else {
                    data = await fetchProducts(currentPage);
                    if (data.products) {
                        setProducts(data.products);
                        setTotalPages(data.totalPages || 1);
                    } else {
                        setProducts(data);
                    }
                }

                sessionStorage.setItem('lastProductsPage', currentPage);

                trackEvent(sessionId, 'PAGE_VIEW', 'Products List', { 
                    pageNumber: currentPage,
                    filterApplied: activeFilter ? activeFilter.label : 'None'
                }, experimentId);

                window.scrollTo(0, 0); 
            } catch (error) {
                console.error("Error loading products:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadProducts();
    }, [currentPage, activeFilter, sessionId, experimentId]);

    const handleJumpPage = (e) => {
        e.preventDefault();
        const targetPage = parseInt(jumpPage);
        if (targetPage >= 1 && targetPage <= totalPages) {
            setCurrentPage(targetPage);
            setJumpPage(""); 
        } else {
            alert(`Please enter a page between 1 and ${totalPages}`);
        }
    };

    const handleFilterSelect = (filterObj) => {
        trackEvent(sessionId, 'CLICK', 'Nutrition Filter Toggle', { 
            filterLabel: filterObj ? filterObj.label : 'All Products' 
        }, experimentId);
        setActiveFilter(filterObj);
        setCurrentPage(1); 
    };

    async function update(product) {
        let returnValue = await addToCart(1, product.id);

        trackEvent(sessionId, 'CLICK', 'Add to Cart', {
            productId: product.id,
            productName: product.name,
            price: product.price,
            success: !!returnValue
        }, experimentId);
        setPopup(returnValue ? "Added To Cart!" : "Item Not Added.");
        if (returnValue) window.dispatchEvent(new Event("cartUpdated"));
        setTimeout(() => setPopup(""), 3000);
    };

    const scrollCategories = (direction) => {
        if (categoryScrollRef.current) {
            trackEvent(sessionId, 'CLICK', 'Category Carousel Scroll', { direction }, experimentId);
            categoryScrollRef.current.scrollBy({
                left: direction === "left" ? -300 : 300,
                behavior: "smooth"
            });
        }
    };

    return (
        <main className="page-container">
            <h1 className="page-title">Products</h1>

            {(showCategories || restrictionMode === 'GREY') && (
                <div className={`carousel-container category-carousel ${!showCategories && restrictionMode === 'GREY' ? 'restricted-ghost-mode' : ''}`}>
                    <button className="carousel-arrow left" onClick={() => scrollCategories("left")}>
                        <FaChevronLeft />
                    </button>
                    <div className="carousel-track category-track" ref={categoryScrollRef}>
                        {CATEGORIES.filter(cat => {
                            try {
                                const rawHidden = localStorage.getItem("hiddenCategories");
            
                                // 1. If nothing is hidden, show everything
                                if (!rawHidden) return true;

                                // 2. Safety Check: If it's a valid JSON array, parse it. 
                                const hiddenList = rawHidden.startsWith('[') 
                                    ? JSON.parse(rawHidden) 
                                    : rawHidden.split(',');

                                // 3. Hide the category if its label is in the list
                                return !hiddenList.includes(cat.label);
                            } catch (e) {
                                console.error("Filter Error:", e);
                                return true;
                            }
                        }).map((cat) => (
                            <CategoryDropdown
                                key={cat.label}
                                cat={cat}
                                sessionId={sessionId}
                                experimentId={experimentId}
                            />
                        ))}
                    </div>
                    <button className="carousel-arrow right" onClick={() => scrollCategories("right")}>
                        <FaChevronRight />
                    </button>
                </div>
            )}

            <div className="store-layout">
                {(showFilters || restrictionMode === 'GREY') && (
                    <aside className={`filter-sidebar ${!showFilters && restrictionMode === 'GREY' ? 'restricted-ghost-mode' : ''}`}>
                        <h3 className="filter-title">Filter By</h3>
                        
                        <button 
                            className={`filter-all-btn ${!activeFilter ? 'active' : ''}`}
                            onClick={() => handleFilterSelect(null)}
                        >
                            All Products
                        </button>

                        {NUTRITION_GROUPS.map((group) => (
                            <div key={group.label} className="filter-accordion-section">
                                <button 
                                    className={`filter-group-trigger ${activeFilter?.column === group.column ? 'active-group' : ''}`}
                                    onClick={() => setExpandedGroup(expandedGroup === group.label ? null : group.label)}
                                >
                                    {group.label}
                                    <span className={`filter-chevron ${expandedGroup === group.label ? 'up' : 'down'}`}>▾</span>
                                </button>
                                
                                {expandedGroup === group.label && (
                                    <div className="filter-sub-menu">
                                        {group.options.map((opt) => (
                                            <button
                                                key={opt.label}
                                                className={`filter-sub-option ${activeFilter?.label === opt.label ? 'active' : ''}`}
                                                onClick={() => handleFilterSelect({ ...opt, column: group.column })}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </aside>
                )}

                <div 
                    className={`products-grid ${isLoading ? 'grid-loading' : ''} ${!showFilters && restrictionMode !== 'GREY' ? 'full-width-grid' : ''}`} 
                    key={currentPage}
                >
                    {products.length > 0 ? (
                        products.map((product) => (
                            <div key={product.id} className="product-card-wrapper">
                                <Link
                                    className="product-item"
                                    to={`/product/${product.id}/${slugify(product.name)}`}
                                    state={{ from: "/products" }}
                                    onClick={() => trackEvent(sessionId, 'CLICK', 'Product Card Image/Title', { productName: product.name }, experimentId)}
                                >
                                    <img src={getProductImage(product.image)} alt={product.name} />
                                    <h2>{product.name}</h2>
                                    <div className="product-info-row">
                                        <p>${Number(product.price?.toString().replace('$', '')).toFixed(2)}</p>
                                        <p>{product.nutrition?.calories ?? 0} Cal</p>
                                    </div>
                                </Link>

                                <div className="product-card-footer">
                                    {(showQuickInfo || restrictionMode === 'GREY') && (
                                      <button
                                          className={`quick-info-btn ${!showQuickInfo && restrictionMode === 'GREY' ? 'restricted-ghost-mode' : ''}`}
                                          onClick={(e) => { e.preventDefault(); toggleNutrition(product); }}
                                      >
                                          +
                                      </button>
                                    )}
                                    <button className="add-to-cart-button" onClick={() => update(product)}>
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                            <circle cx="9" cy="21" r="1"></circle>
                                            <circle cx="20" cy="21" r="1"></circle>
                                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                                        </svg>
                                    </button>
                                    {activeNutritionId === product.id && (
                                        <div className="nutrition-quick-menu">
                                            <h4>Quick Nutrition</h4>
                                            <p><strong>Calories:</strong> {product.nutrition?.calories ?? 0}</p>
                                            <p><strong>Ingredients:</strong> {product.nutrition?.ingredients ? product.nutrition.ingredients.substring(0, 70) + "..." : "N/A"}</p>
                                            <button className="close-menu-btn" onClick={() => setActiveNutritionId(null)}>Close</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        !isLoading && <p>No Products match this filter.</p>
                    )}
                </div>
            </div>

            <div className="pagination-container">
                <button
                    className="pagination-button"
                    onClick={() => {
                        setCurrentPage(currentPage - 1);
                        trackEvent(sessionId, 'CLICK', 'Pagination', { direction: 'previous', targetPage: currentPage - 1 }, experimentId);
                    }}
                    disabled={currentPage === 1 || isLoading}
                >
                    <span>←</span> Previous
                </button>

                <div className="pagination-jump-container">
                    <span className="page-indicator">Page <strong>{currentPage}</strong> of {totalPages}</span>
                    <form onSubmit={handleJumpPage} className="page-jump-form">
                        <input 
                            type="number" 
                            placeholder="Go to..." 
                            value={jumpPage} 
                            onChange={(e) => setJumpPage(e.target.value)}
                            min="1"
                            max={totalPages}
                        />
                        <button type="submit" className="jump-btn">Go</button>
                    </form>
                </div>

                <button
                    className="pagination-button"
                    onClick={() => {
                        setCurrentPage(currentPage + 1);
                        trackEvent(sessionId, 'CLICK', 'Pagination', { direction: 'next', targetPage: currentPage + 1 }, experimentId);
                    }}
                    disabled={currentPage === totalPages || isLoading}
                >
                    Next <span>→</span>
                </button>
            </div>

            {popup && <div className="popup"><h2>{popup}</h2></div>}
        </main>
    );
};

export default Products;